import { QueryClient, QueryFunction } from '@tanstack/react-query';

import { ApiError, authedFetch } from '@/lib/http';

export { ApiError };

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new ApiError(res.status, text);
  }
}

/** Cold start: the serverless function couldn't reach a warm DB in time. The API
 * entry returns 503 (older instances may still return 500 with this body). The
 * request never reached business logic, so retrying is always safe. */
function isColdStart(status: number, body: string): boolean {
  if (status === 503) return true;
  if (status !== 500) return false;
  try {
    return JSON.parse(body)?.error === 'Service unavailable';
  } catch {
    return false;
  }
}

/** Reads are idempotent — retry any server error or network failure. */
function isRetryableQueryError(error: unknown): boolean {
  if (error instanceof ApiError) return error.status >= 500;
  if (error instanceof TypeError) return true; // network failure (fetch rejected)
  return false;
}

const MAX_COLD_START_RETRIES = 2;
/** Backoff so a waking DB has time: 1s, 2s, 4s … capped at 6s. */
function backoffMs(attempt: number): number {
  return Math.min(1000 * 2 ** attempt, 6000);
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined
): Promise<Response> {
  const fetchOptions: RequestInit = {
    method,
    headers: data ? { 'Content-Type': 'application/json' } : undefined,
    body: data ? JSON.stringify(data) : undefined,
  };

  // Cold-start retry with backoff. Only the cold-start signal is retried — the
  // request never reached business logic in that case, so there's no double-submit
  // risk. Real 4xx/5xx errors surface immediately.
  for (let attempt = 0; ; attempt++) {
    const res = await authedFetch(url, fetchOptions);
    if (res.ok) return res;
    const body = await res.text();
    if (attempt < MAX_COLD_START_RETRIES && isColdStart(res.status, body)) {
      await new Promise(resolve => setTimeout(resolve, backoffMs(attempt)));
      continue;
    }
    throw new ApiError(res.status, body);
  }
}

/** Fetch JSON, throwing a typed ApiError on a non-2xx response so the central
 * query retry can recover cold starts (5xx/503). Use this in custom queryFns
 * instead of raw `fetch` + `throw new Error(...)`, which produces an untyped
 * error the retry logic can't classify. Pair with NOT setting `retry: false`. */
export async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: 'include', ...init });
  await throwIfResNotOk(res);
  return res.json() as Promise<T>;
}

type UnauthorizedBehavior = 'returnNull' | 'throw';
export const getQueryFn: <T>(options: { on401: UnauthorizedBehavior }) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join('/') as string, {
      credentials: 'include',
    });

    if (unauthorizedBehavior === 'returnNull' && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: 'throw' }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      // Cold-start resilience: retry reads on 5xx/network (idempotent + safe) so a
      // suspended-DB wake surfaces as a brief spinner, not an error. 4xx never retries.
      retry: (failureCount, error) => failureCount < 3 && isRetryableQueryError(error),
      retryDelay: attemptIndex => backoffMs(attemptIndex),
    },
    mutations: {
      // apiRequest handles cold-start retry for mutations; don't double-retry here.
      retry: false,
    },
  },
});
